import { type VariantProps, cva } from "class-variance-authority"
import { ReactNode } from "react"

import { cn } from "@/lib/utils"
import { Icon } from "@tabler/icons-react"

import { Spinner } from "./Spinner"
import { PolymorphicComponentProps } from "./types"

const buttonVariants = cva(
  cn(
    "group/button",
    "inline-flex items-center justify-center gap-2",
    "whitespace-nowrap",
    "rounded",
    "transition-colors",
    "focus-outline",
    "aria-disabled:pointer-events-none",
    "border border-solid",
  ),
  {
    variants: {
      variant: {
        primary: [
          "text-white",
          "border-transparent",
          "bg-colors-button-primary-default",

          "enabled:hover:bg-colors-button-primary-hover",
          "aria-expanded:bg-colors-button-primary-hover",

          "enabled:active:bg-colors-button-primary-active",
          "enabled:aria-pressed:bg-colors-button-primary-active",
        ],

        secondary: [
          "text-colors-text-link",
          "border-colors-border-button",
          "bg-colors-button-secondary-default",

          "enabled:hover:bg-colors-button-secondary-hover",
          "aria-expanded:bg-colors-button-secondary-hover",

          "enabled:active:bg-colors-button-secondary-active",
          "enabled:aria-pressed:bg-colors-button-secondary-active",
        ],
        neutral: [
          "text-colors-text-primary",
          "border-colors-text-disabled",
          "bg-colors-button-neutral-default",

          "enabled:hover:bg-colors-button-neutral-hover",
          "aria-expanded:bg-colors-button-neutral-hover",

          "enabled:active:bg-colors-button-neutral-active",
          "enabled:aria-pressed:bg-colors-button-neutral-active",
        ],
        danger: [
          "text-colors-text-error",
          "border-colors-border-error",
          "bg-colors-button-danger-default",

          "enabled:hover:bg-colors-button-danger-hover",
          "aria-expanded:bg-colors-button-danger-hover",

          "enabled:active:bg-colors-button-danger-active",
          "enabled:aria-pressed:bg-colors-button-danger-active",
        ],
      },
      size: {
        small: "py-0.5 px-3 text-body2",
        medium: "py-1 px-4 text-body1",
        large: "py-2 px-5 text-h4",
      },
      iconPosition: {
        left: "",
        right: "flex-row-reverse",
      },
      iconOnly: {
        true: "",
        false: "",
      },
      loading: {
        true: "",
        false: "",
      },
      disabled: {
        false: "",
        true: "",
      },
      noBorder: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      // loading
      {
        variant: "primary",
        loading: true,
        className: "bg-colors-button-primary-active",
      },
      {
        variant: "secondary",
        loading: true,
        className: "bg-colors-button-secondary-active",
      },
      {
        variant: "neutral",
        loading: true,
        className: "bg-colors-button-neutral-active",
      },
      {
        variant: "danger",
        loading: true,
        className: "bg-colors-button-danger-active",
      },
      // disabled
      {
        disabled: true,
        loading: false,
        className: "text-colors-text-disabled",
      },
      {
        variant: ["secondary", "neutral", "danger"],
        disabled: true,
        loading: false,
        className: "border-grey-400",
      },
      {
        variant: ["primary", "neutral"],
        disabled: true,
        loading: false,
        className: "bg-colors-bg-disabled",
      },
      {
        variant: ["secondary", "danger"],
        disabled: true,
        loading: false,
        className: "bg-white",
      },
      // icon only
      {
        iconOnly: true,
        size: "small",
        className: "p-1.5",
      },
      {
        iconOnly: true,
        size: "medium",
        className: "p-2",
      },
      {
        iconOnly: true,
        size: "large",
        className: "p-3",
      },
      // no border
      {
        variant: ["secondary", "neutral", "danger"],
        noBorder: true,
        className: "border-transparent",
      },
      {
        variant: ["secondary", "neutral", "danger"],
        noBorder: true,
        loading: false,
        disabled: [false, true],
        className: "bg-transparent",
      },
    ],

    defaultVariants: {
      variant: "primary",
      size: "medium",
      iconPosition: "left",
      iconOnly: false,
      loading: false,
      disabled: false,
    },
  },
)

const contentVariants = cva("inline-flex items-center justify-center gap-2", {
  variants: {
    variant: {
      primary: "",
      secondary: "",
      neutral: "",
      danger: "",
    },
    loading: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      variant: "primary",
      loading: true,
      className: "text-white/50",
    },
    {
      variant: "secondary",
      loading: true,
      className: "text-colors-text-link/50",
    },
    {
      variant: "neutral",
      loading: true,
      className: "text-colors-text-primary/50",
    },
    {
      variant: "danger",
      loading: true,
      className: "text-colors-text-error/50",
    },
  ],
})

export type ButtonProps<C> = PolymorphicComponentProps<
  C,
  VariantProps<typeof buttonVariants> & {
    icon?: Icon
    className?: string
    children?: ReactNode
  }
>

type ButtonComponent = (<C = "button">(
  props: ButtonProps<C>,
) => React.ReactElement<any> | null) & {
  displayName?: string
}

const Button = (({
  className,
  variant,
  size,
  loading = false,
  disabled,
  icon,
  iconPosition,
  iconOnly,
  noBorder,
  children,
  component = "button",
  type = "button",
  ...props
}: ButtonProps<"button">) => {
  const IconComponent = icon
  const buttonIcon = IconComponent ? (
    loading ? (
      <Spinner size="1em" />
    ) : (
      <IconComponent className="shrink-0" />
    )
  ) : null

  const Comp = component

  return (
    <Comp
      className={cn(
        buttonVariants({
          variant,
          size,
          iconPosition,
          iconOnly,
          disabled,
          loading,
          noBorder,
          className,
        }),
      )}
      type={type}
      disabled={disabled || loading || false}
      {...props}
    >
      {loading && !IconComponent ? (
        <Spinner className="absolute h-fit" size="1em" />
      ) : (
        buttonIcon
      )}
      {!iconOnly && (
        <span
          className={cn(contentVariants({ variant, loading }), {
            invisible: loading && !IconComponent,
          })}
        >
          {children}
        </span>
      )}
    </Comp>
  )
}) as ButtonComponent

Button.displayName = "Button"

export { Button, buttonVariants }
